'use client';

import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BillingDetails, StripeProvider } from '@/lib/stripe/StripeProvider';
import { Banner, BannerDescription } from '@/ui-lib/banner';
import { Button } from '@/ui-lib/button';
import { Loader } from '@/ui-lib/loader';

import { RuntimeEnvProvider } from '../runtime-env';

async function createSetupIntent(): Promise<{ clientSecret: string }> {
    const response = await fetch('/app-api/payment-methods/setup-intent', {
        method: 'POST',
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create setup intent');
    }
    return response.json();
}

async function confirmPaymentMethod(setupIntentId: string): Promise<void> {
    const response = await fetch('/app-api/payment-methods/confirm', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ setupIntentId }),
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to confirm payment method');
    }
}

interface AddPaymentMethodFormProps {
    onSuccess: () => void;
    onCancel: () => void;
    defaultBillingDetails?: BillingDetails;
}

function AddPaymentMethodForm({ onSuccess, onCancel, defaultBillingDetails }: AddPaymentMethodFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setErrorMessage(null);

        try {
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setErrorMessage(submitError.message || 'Failed to submit payment details');
                setIsProcessing(false);
                return;
            }

            const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                redirect: 'if_required',
            });

            if (error) {
                setErrorMessage(error.message || 'Failed to confirm payment method');
                setIsProcessing(false);
                return;
            }

            if (setupIntent && setupIntent.status === 'succeeded') {
                await confirmPaymentMethod(setupIntent.id);
                toast.success('Payment method added successfully');
                onSuccess();
            }
        } catch (error: any) {
            setErrorMessage(error.message || 'An unexpected error occurred');
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col ">
            <div className="space-y-4 overflow-y-auto max-h-[60vh] flex-1 pr-2">
                <PaymentElement
                    options={{
                        fields: {
                            billingDetails: {
                                name: 'auto',
                                email: 'auto',
                            },
                        },
                        defaultValues: defaultBillingDetails
                            ? {
                                  billingDetails: {
                                      name: defaultBillingDetails.name,
                                      email: defaultBillingDetails.email,
                                      phone: defaultBillingDetails.phone,
                                      address: defaultBillingDetails.address,
                                  },
                              }
                            : undefined,
                    }}
                />
            </div>

            {errorMessage && (
                <Banner variant="error">
                    <BannerDescription>{errorMessage}</BannerDescription>
                </Banner>
            )}

            <div className="flex gap-3 justify-end pt-4 border-t border-neutral-800 mt-4 bg-neutral-950">
                <Button variant="secondary" size="sm" onClick={onCancel} disabled={isProcessing} type="button">
                    Cancel
                </Button>
                <Button
                    variant="cta"
                    size="sm"
                    type="submit"
                    disabled={!stripe || isProcessing}
                    isLoading={isProcessing}
                    data-testid="save-payment-method"
                >
                    {isProcessing ? 'Processing...' : 'Add Payment Method'}
                </Button>
            </div>
        </form>
    );
}

interface AddPaymentMethodDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: () => void;
    /** Optional billing details to pre-fill the form */
    defaultBillingDetails?: BillingDetails;
}

export function AddPaymentMethodDialog({
    open,
    onOpenChange,
    onSuccess,
    defaultBillingDetails,
}: AddPaymentMethodDialogProps) {
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch setup intent when dialog opens
    useEffect(() => {
        if (open) {
            // Always create a new setup intent when dialog opens
            setIsLoading(true);
            setClientSecret(null);
            createSetupIntent()
                .then(({ clientSecret: secret }) => {
                    setClientSecret(secret);
                })
                .catch((error: any) => {
                    toast.error(error.message || 'Failed to initialize payment form');
                    onOpenChange(false);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            // Reset when closing
            setClientSecret(null);
        }
    }, [open, onOpenChange]);

    const handleSuccess = () => {
        setClientSecret(null);
        onSuccess();
        onOpenChange(false);
    };

    const handleCancel = () => {
        setClientSecret(null);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent theme="dark" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-neutral-100">Add Payment Method</DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Add a new credit card or bank account to your account
                    </DialogDescription>
                </DialogHeader>

                {isLoading || !clientSecret ? (
                    <div className="py-8 flex items-center justify-center">
                        <Loader mode="light" size="md" />
                    </div>
                ) : (
                    <RuntimeEnvProvider>
                        <StripeProvider clientSecret={clientSecret}>
                            <AddPaymentMethodForm
                                onSuccess={handleSuccess}
                                onCancel={handleCancel}
                                defaultBillingDetails={defaultBillingDetails}
                            />
                        </StripeProvider>
                    </RuntimeEnvProvider>
                )}
            </DialogContent>
        </Dialog>
    );
}
